// @flow

import * as React from 'react';

import moment from 'moment';
import { toUnicode } from 'punycode.js';

import { selectOneProductErrors, type DecoratedProduct } from '@gandi/cart';

import { useTranslator, localeSelector, type Locale } from '@gandi/translate';

import { useAppSelector } from 'src/hooks';

import ProductErrorItem from './components/ProductErrorItem';
import ProductItem from './components/ProductItem';

type Props = {
  product: DecoratedProduct,
};

const formatDuration = ({
  duration,
  locale,
}: {
  duration: number,
  locale: Locale,
}): any => {
  const formattedDuration = {
    years: duration >= 12 ? Math.floor(duration / 12) : 0,
    months: duration >= 12 ? Math.floor(duration % 12) : Math.floor(duration),
    days: Math.floor((duration % 1) * 30),
  };

  moment.locale(locale);

  return Object.keys(formattedDuration)
    .filter(k => formattedDuration[k] > 0)
    .map(k => {
      if (formattedDuration[k] > 0) {
        return moment.duration(formattedDuration[k], k).humanize();
      }
      return undefined;
    })
    .join(', ');
};

export function MarketplaceCreate({ product }: Props): React.MixedElement {
  const { __ } = useTranslator();

  const locale = useAppSelector(localeSelector);
  const productErrors = useAppSelector(state =>
    selectOneProductErrors(state, {
      productId: product.id,
    }),
  );

  const detail = __(
    'Will be installed on %(fqdn)s for a period of %(duration)s',
    {
      fqdn: toUnicode(product.payload.fqdn || ''),
      duration: formatDuration({
        duration: product.payload.duration || 0,
        locale,
      }),
    },
  );
  const label = product.label || '';
  if (productErrors.length > 0)
    return (
      <ProductErrorItem
        key={`${product.id}-error-row`}
        label={label}
        detail={detail}
        productId={product.id}
        errors={productErrors}
      />
    );

  return (
    <ProductItem
      key={`${product.id}-row`}
      hasTaxes={product.hasTaxes}
      priceBeforeTaxes={
        product.isWorking ? undefined : product.total?.before_taxes
      }
      productId={product.id}
      label={label}
      detail={detail}
    />
  );
}
